import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

import RequestLoanSummaryBox from "./RequestLoanSummaryBox";
import { TRACKING_CARD_SX } from "./trackingStyles";

const MetaItem = ({ label, value, mono = false }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={600}
      fontFamily={mono ? "monospace" : "inherit"}
    >
      {value}
    </Typography>
  </Box>
);

MetaItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  mono: PropTypes.bool,
};

const HeroIllustration = ({ small }) => (
  <Box
    sx={{
      width: small ? 72 : 96,
      height: small ? 72 : 96,
      borderRadius: 3,
      background: (theme) =>
        theme.palette.mode === "dark"
          ? "linear-gradient(135deg, rgba(76,175,80,0.2), rgba(255,193,7,0.15))"
          : "linear-gradient(135deg, #E8F5E9 0%, #FFF8E1 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      flexShrink: 0,
    }}
  >
    <HomeWorkOutlinedIcon
      sx={{ fontSize: small ? 34 : 42, color: "success.dark", opacity: 0.85 }}
    />
    <WbSunnyOutlinedIcon
      sx={{
        position: "absolute",
        top: 6,
        left: 6,
        fontSize: small ? 18 : 22,
        color: "warning.main",
      }}
    />
  </Box>
);

const HeroContactButtons = ({ model, fullWidth = false }) => {
  const phone = model?.expertPhone;
  const telHref = phone ? `tel:${String(phone).replace(/\s/g, "")}` : undefined;

  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ChatOutlinedIcon />}
        fullWidth={fullWidth}
        disabled
        sx={{ borderRadius: 2 }}
      >
        پیام به کارشناس
      </Button>
      <Button
        variant="contained"
        size="small"
        startIcon={<PhoneOutlinedIcon />}
        fullWidth={fullWidth}
        component={telHref ? "a" : "button"}
        href={telHref}
        disabled={!telHref}
        sx={{ borderRadius: 2 }}
      >
        تماس با کارشناس
      </Button>
    </Stack>
  );
};

const HeroMainContent = ({ model, isMobile }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography
      variant={isMobile ? "subtitle1" : "h6"}
      fontWeight={800}
      sx={{ mb: 0.75, lineHeight: 1.35 }}
    >
      {model.requestTitle}
    </Typography>

    <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ mb: 1.25 }}>
      <Chip
        label={model.currentStageTitle}
        color="primary"
        size="small"
        sx={{ fontWeight: 700, maxWidth: "100%" }}
      />
      {model.showRequestStatusChip ? (
        <Chip
          label={model.statusLabel}
          color={model.statusChipColor || "default"}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      ) : null}
    </Stack>

    <Stack spacing={0.75} sx={{ mb: isMobile ? 1 : 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        پیشرفت پرونده:{" "}
        <Box
          component="span"
          fontWeight={800}
          color={`${model.progressColor || "primary"}.main`}
        >
          {model.progressPercent.toLocaleString("fa-IR")}٪
        </Box>
      </Typography>
      <LinearProgress
        variant="determinate"
        value={model.progressPercent}
        color={model.progressColor || "primary"}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: "grey.200",
          "& .MuiLinearProgress-bar": { borderRadius: 4 },
        }}
      />
    </Stack>

    {!isMobile ? (
      <>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <MetaItem label="کد درخواست" value={model.requestCode} mono />
          </Grid>
          <Grid item xs={6} sm={3}>
            <MetaItem label="کارشناس پرونده" value={model.expertName} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <MetaItem label="آخرین بروزرسانی" value={model.lastUpdateLabel} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <MetaItem label="تاریخ ثبت" value={model.requestDateLabel} />
          </Grid>
        </Grid>
        <HeroContactButtons model={model} />
      </>
    ) : null}
  </Box>
);

const RequestStatusHeroCard = ({ model, isMobile }) => {
  const desktopHero = (
    <Box sx={{ ...TRACKING_CARD_SX, p: { xs: 2, md: 2.5 } }}>
      <Grid container spacing={2.5} alignItems="stretch">
        <Grid item xs={12} md={8}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <HeroIllustration />
            <HeroMainContent model={model} isMobile={false} />
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <RequestLoanSummaryBox loanSummary={model.loanSummary} />
        </Grid>
      </Grid>
    </Box>
  );

  if (!isMobile) {
    return desktopHero;
  }

  return (
    <Box sx={{ ...TRACKING_CARD_SX, overflow: "hidden" }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <HeroIllustration small />
          <HeroMainContent model={model} isMobile />
        </Stack>
      </Box>

      <Divider />

      <Accordion
        disableGutters
        elevation={0}
        defaultExpanded={false}
        sx={{ "&:before": { display: "none" } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 48 }}>
          <Typography variant="body2" fontWeight={700}>
            اطلاعات مالی و وام مهرسان
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
          <RequestLoanSummaryBox loanSummary={model.loanSummary} compact />
        </AccordionDetails>
      </Accordion>

      <Divider />

      <Accordion disableGutters elevation={0} sx={{ "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 48 }}>
          <Typography variant="body2" fontWeight={700}>
            مشاهده جزئیات
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
          <Stack spacing={1.25}>
            <MetaItem label="کد درخواست" value={model.requestCode} mono />
            <MetaItem label="کارشناس پرونده" value={model.expertName} />
            <MetaItem label="آخرین بروزرسانی" value={model.lastUpdateLabel} />
            <MetaItem label="تاریخ ثبت" value={model.requestDateLabel} />
            {model.currentStageDescription ? (
              <Typography variant="body2" color="text.secondary" lineHeight={1.65}>
                {model.currentStageDescription}
              </Typography>
            ) : null}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

RequestStatusHeroCard.propTypes = {
  model: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
};

export default RequestStatusHeroCard;
